import type { GenerationPromptModifierKey } from "../store"

export type DbModifierOption = {
  label: string
  group: string
  /** Public https URL of the picker preview image, or null for a text tile. */
  previewUrl: string | null
}

export type DbModifierOptions = Partial<
  Record<GenerationPromptModifierKey, DbModifierOption[]>
>

export interface PromptModifierRow {
  modifier_key: string
  option_label: string
  option_group: string | null
  sort_order: number
  preview_url: string | null
}

export const MODIFIER_KEYS: GenerationPromptModifierKey[] = [
  "position",
  "outfit",
  "location",
  "bodyType",
  "breasts",
  "facialExpression",
  "hairStyle",
  "race",
  "age",
  "eyeColor",
]

export const PROMPT_MODIFIER_COLUMNS =
  "modifier_key, option_label, option_group, sort_order, preview_url"
/** Columns that exist before migration 20261021203000 adds preview_url. */
export const PROMPT_MODIFIER_BASE_COLUMNS = "modifier_key, option_label, option_group, sort_order"

/** Postgres undefined_column: PostgREST returns it when the select names a missing column. */
const UNDEFINED_COLUMN = "42703"

type PromptModifierQueryResult = {
  data: unknown[] | null
  error: { code?: string } | null
}

/**
 * Runs the prompt_modifiers select. The frontend deploys (Coolify) before the
 * backend CI applies migrations, so production can briefly lack preview_url
 * (PLE-917). In that window, re-select without it and render text tiles
 * instead of failing the whole query.
 */
export async function loadPromptModifierRows(
  select: (columns: string) => PromiseLike<PromptModifierQueryResult>
): Promise<PromptModifierRow[]> {
  const result = await select(PROMPT_MODIFIER_COLUMNS)
  if (result.error?.code !== UNDEFINED_COLUMN) {
    if (result.error) throw result.error
    return (result.data ?? []) as PromptModifierRow[]
  }
  const base = await select(PROMPT_MODIFIER_BASE_COLUMNS)
  if (base.error) throw base.error
  return ((base.data ?? []) as Omit<PromptModifierRow, "preview_url">[]).map((row) => ({
    ...row,
    preview_url: null,
  }))
}

/** Accept only a non-empty https URL; anything else renders as a text tile. */
function toPreviewUrl(value: unknown): string | null {
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  if (!trimmed) return null
  try {
    return new URL(trimmed).protocol === "https:" ? trimmed : null
  } catch {
    return null
  }
}

/**
 * Group prompt_modifiers rows (already sorted) by modifier key. Every key gets
 * an array — an empty array means "no LoRA-backed options exist for this
 * category" (the UI hides that card), which is distinct from the hook's
 * `options === undefined` (still loading / failed → static defaults).
 */
export function groupPromptModifierRows(rows: readonly PromptModifierRow[]): DbModifierOptions {
  const grouped: Record<string, DbModifierOption[]> = Object.fromEntries(
    MODIFIER_KEYS.map((key) => [key, []])
  )
  for (const row of rows) {
    if (!Object.hasOwn(grouped, row.modifier_key)) continue
    const options = grouped[row.modifier_key]
    grouped[row.modifier_key] = [
      ...options,
      {
        label: row.option_label,
        group: row.option_group ?? "Other",
        previewUrl: toPreviewUrl(row.preview_url),
      },
    ]
  }
  return grouped as DbModifierOptions
}
